import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import {
  TellentAssistantConfigService,
  TellentAssistantFacade,
} from '@core/tellent-assistant-logic';
import {
  TellentAssistantEmptyStateComponent,
  TellentAssistantHeaderComponent,
  TellentAssistantInputComponent,
  TellentAssistantMessageComponent,
  TellentAssistantThreadDrawerComponent,
  TellentAssistantContextPreviewComponent,
  TellentAssistantThinkingIndicatorComponent,
} from '@core/tellent-assistant-ui';
import { AppKind, ORG_GUID } from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  WindowRef,
} from '@core/user-interface-legacy';

@Component({
  selector: 'tellent-assistant-chat-panel',
  standalone: true,
  imports: [
    TellentAssistantHeaderComponent,
    TellentAssistantEmptyStateComponent,
    TellentAssistantInputComponent,
    TellentAssistantThreadDrawerComponent,
    TellentAssistantMessageComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    TellentAssistantContextPreviewComponent,
    TellentAssistantThinkingIndicatorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tellent-assistant-chat-panel.component.html',
  styleUrls: ['./tellent-assistant-chat-panel.component.less'],
})
export class TellentAssistantChatPanelComponent {
  protected readonly facade = inject(TellentAssistantFacade);
  protected readonly config = inject(TellentAssistantConfigService);
  protected readonly __devMode = false;

  private readonly i18n = inject(I18nService);
  private readonly windowRef = inject(WindowRef);
  private readonly orgGuid = inject(ORG_GUID, { optional: true });
  private readonly urlBuilder = injectTellentAppUrlBuilder();

  protected readonly drawerOpen = signal<boolean>(false);
  protected readonly drawerPositions = [
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
  ] satisfies ConnectionPositionPair[];

  public readonly close = output<void>();
  public readonly isDeleting = (id: string): boolean => this.facade.isThreadDeleting(id);

  public deleteThread(id: string): void {
    this.facade.deleteThread(id);
  }

  protected send(text: string): void {
    this.facade.send(text);
  }

  protected newThread(): void {
    this.facade.newThread();
  }

  protected selectThread(id: string): void {
    this.facade.switchThread(id);
  }

  protected onResolve(ev: { id: string; payload: unknown }): void {
    this.facade.resolveInterrupt(ev.id, ev.payload);
  }

  protected openHelpCenter(): void {
    const url = this.i18n.translate('app.support_fab.help_center_settings_url');
    this.windowRef.nativeWindow.open(url, '_blank');
  }

  protected openIntelligenceSettings(): void {
    if (!this.orgGuid) return;
    const url = this.urlBuilder(AppKind.TellentAdmin, { id: 'intelligence' }, this.orgGuid);
    this.windowRef.nativeWindow.location = url;
  }
}
